import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

/**
 * 操作性条件反射动画 - 斯金纳箱实验可视化
 * 
 * 核心效果 / Core Effects:
 * 1. 老鼠按杠杆获取食物动画 / Mouse pressing lever for food animation
 * 2. 强化时间表展示 / Reinforcement schedule display
 * 3. 学习曲线可视化 / Learning curve visualization
 * 4. 实验过程逐步演示 / Step-by-step experiment demonstration
 * 
 * 时长 / Duration: 330 帧 (11 秒 @ 30fps)
 * 评分目标 / Score Target: ≥8/10
 */

// 配色方案 / Color Scheme
const BG = '#0f1419';
const PRIMARY = '#4A90E2';
const SECONDARY = '#FF6B6B';
const ACCENT = '#FFD93D';
const SUCCESS = '#10B981';
const MOUSE_COLOR = '#9CA3AF';
const LEVER_COLOR = '#6B7280';
const FOOD_COLOR = '#F59E0B';

interface OperantConditioningAnimationProps {
  primaryColor?: string;
  secondaryColor?: string;
  backgroundColor?: string;
}

export const OperantConditioningAnimation: React.FC<OperantConditioningAnimationProps> = ({
  primaryColor = PRIMARY,
  secondaryColor = SECONDARY,
  backgroundColor = BG,
}) => {
  const frame = useCurrentFrame();
  const { fps, width, height } = useVideoConfig();

  // ===== 动画时间轴 / Animation Timeline =====
  
  // 标题淡入 (0-30 帧)
  const titleOpacity = interpolate(frame, [0, 30], [0, 1], {
    extrapolateRight: 'clamp',
  });
  const titleScale = spring({
    frame,
    from: 0.8,
    to: 1,
    fps,
    config: { damping: 12, stiffness: 100 },
  });

  // 斯金纳箱出现 (30-90 帧)
  const boxOpacity = spring({
    frame: frame - 30,
    from: 0,
    to: 1,
    fps,
    config: { damping: 15, stiffness: 100 },
  });

  // 老鼠和杠杆动画 (60-180 帧) - 循环
  const cycleFrame = frame % 120;
  const mousePress = interpolate(
    cycleFrame,
    [0, 40, 80, 120],
    [0, 1, 0, 0],
    { extrapolateRight: 'clamp' }
  );
  const leverPress = interpolate(
    cycleFrame,
    [30, 50, 70, 90],
    [0, 1, 1, 0],
    { extrapolateRight: 'clamp' }
  );
  const foodDispense = interpolate(
    cycleFrame,
    [50, 70, 90, 110],
    [0, 1, 1, 0],
    { extrapolateRight: 'clamp' }
  );

  // 强化时间表 (150-240 帧)
  const scheduleOpacity = interpolate(frame, [150, 180], [0, 1], {
    extrapolateRight: 'clamp',
  });

  // 学习曲线 (240-330 帧)
  const curveOpacity = interpolate(frame, [240, 270], [0, 1], {
    extrapolateRight: 'clamp',
  });
  const curveProgress = interpolate(frame, [270, 320], [0, 1], {
    extrapolateRight: 'clamp',
  });

  // 结果揭示 (300-330 帧)
  const resultOpacity = interpolate(frame, [300, 320], [0, 1], {
    extrapolateRight: 'clamp',
  });

  // ===== 斯金纳箱位置 / Skinner Box Position =====
  const boxX = width / 2 - 350;
  const boxY = height / 2 - 100;
  const boxWidth = 400;
  const boxHeight = 350;

  // 老鼠位置 / Mouse Position
  const mouseX = boxX + 80;
  const mouseY = boxY + boxHeight - 100;
  const mouseSize = 60;

  // 杠杆位置 / Lever Position
  const leverX = boxX + 280;
  const leverY = boxY + boxHeight - 80;

  // 食物槽位置 / Food Dispenser Position
  const foodX = boxX + 320;
  const foodY = boxY + boxHeight - 50;

  // ===== 学习曲线数据 / Learning Curve Data =====
  const learningData = [
    { trial: 1, rate: 0 },
    { trial: 5, rate: 15 },
    { trial: 10, rate: 35 },
    { trial: 15, rate: 55 },
    { trial: 20, rate: 70 },
    { trial: 25, rate: 82 },
    { trial: 30, rate: 90 },
    { trial: 35, rate: 95 },
    { trial: 40, rate: 98 },
  ];

  // ===== 强化时间表数据 / Reinforcement Schedules =====
  const schedules = [
    {
      name: '固定间隔',
      nameEn: 'Fixed Interval (FI)',
      example: '每隔 5 分钟按杠杆获得食物',
      color: PRIMARY,
      icon: '⏱️',
    },
    {
      name: '可变间隔',
      nameEn: 'Variable Interval (VI)',
      example: '平均 5 分钟但时间随机',
      color: SECONDARY,
      icon: '🎲',
    },
    {
      name: '固定比率',
      nameEn: 'Fixed Ratio (FR)',
      example: '每按 5 次杠杆获得食物',
      color: ACCENT,
      icon: '📊',
    },
    {
      name: '可变比率',
      nameEn: 'Variable Ratio (VR)',
      example: '平均按 5 次但次数随机',
      color: SUCCESS,
      icon: '🎰',
    },
  ];

  // ===== 计数器 / Counter =====
  const pressCount = Math.floor(frame / 60);

  return (
    <AbsoluteFill
      style={{
        background: backgroundColor,
        fontFamily: 'Inter, system-ui, sans-serif',
        color: '#e5e7eb',
        display: 'flex',
        flexDirection: 'column',
        padding: 50,
      }}
    >
      {/* 标题 / Title */}
      <div
        style={{
          textAlign: 'center',
          opacity: titleOpacity,
          transform: `scale(${titleScale})`,
          marginBottom: 40,
        }}
      >
        <h1
          style={{
            fontSize: 68,
            fontWeight: 800,
            margin: 0,
            background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            letterSpacing: 2,
          }}
        >
          操作性条件反射
        </h1>
        <p
          style={{
            fontSize: 28,
            color: '#94a3b8',
            marginTop: 12,
          }}
        >
          Operant Conditioning • 斯金纳箱实验
        </p>
      </div>

      {/* 主内容区 / Main Content */}
      <div
        style={{
          display: 'flex',
          flex: 1,
          gap: 60,
        }}
      >
        {/* 左侧：斯金纳箱 / Left: Skinner Box */}
        <div
          style={{
            flex: 1,
            position: 'relative',
          }}
        >
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              opacity: boxOpacity,
            }}
          >
            {/* 斯金纳箱 / Skinner Box */}
            <svg width={boxWidth + 100} height={boxHeight + 100}>
              {/* 箱子 / Box */}
              <rect
                x={0}
                y={0}
                width={boxWidth}
                height={boxHeight}
                stroke={primaryColor}
                strokeWidth={3}
                fill={`${primaryColor}08`}
                rx={10}
              />

              {/* 杠杆 / Lever */}
              <g>
                {/* 杠杆底座 / Lever Base */}
                <rect
                  x={leverX - boxX}
                  y={leverY - boxY + 10}
                  width={40}
                  height={30}
                  fill={LEVER_COLOR}
                  rx={5}
                />
                {/* 杠杆 / Lever Bar */}
                <rect
                  x={leverX - boxX}
                  y={leverY - boxY - 20 * (1 - leverPress)}
                  width={40}
                  height={8}
                  fill={ACCENT}
                  rx={4}
                  style={{
                    transform: `rotate(${leverPress * 15}deg)`,
                    transformOrigin: `${leverX - boxX + 20}px ${leverY - boxY + 14}px`,
                  }}
                />
              </g>

              {/* 食物槽 / Food Dispenser */}
              <g opacity={foodDispense}>
                {/* 食物槽 / Food Tray */}
                <rect
                  x={foodX - boxX}
                  y={foodY - boxY}
                  width={35}
                  height={25}
                  fill={LEVER_COLOR}
                  rx={5}
                />
                {/* 食物颗粒 / Food Pellet */}
                <circle
                  cx={foodX - boxX + 17}
                  cy={foodY - boxY - 10}
                  r={8}
                  fill={FOOD_COLOR}
                >
                  <animate
                    attributeName="cy"
                    from={foodY - boxY - 30}
                    to={foodY - boxY - 10}
                    dur="0.3s"
                    fill="freeze"
                  />
                </circle>
              </g>

              {/* 老鼠 / Mouse */}
              <g>
                {/* 身体 / Body */}
                <ellipse
                  cx={mouseX - boxX + mousePress * 80}
                  cy={mouseY - boxY}
                  rx={mouseSize}
                  ry={mouseSize * 0.6}
                  fill={MOUSE_COLOR}
                />
                {/* 头部 / Head */}
                <circle
                  cx={mouseX - boxX + mouseSize * 0.8 + mousePress * 80}
                  cy={mouseY - boxY - 10}
                  r={mouseSize * 0.4}
                  fill={MOUSE_COLOR}
                />
                {/* 耳朵 / Ears */}
                <circle
                  cx={mouseX - boxX + mouseSize + mousePress * 80}
                  cy={mouseY - boxY - 25}
                  r={mouseSize * 0.15}
                  fill={SECONDARY}
                />
                <circle
                  cx={mouseX - boxX + mouseSize * 0.6 + mousePress * 80}
                  cy={mouseY - boxY - 25}
                  r={mouseSize * 0.15}
                  fill={SECONDARY}
                />
                {/* 眼睛 / Eye */}
                <circle
                  cx={mouseX - boxX + mouseSize + mousePress * 80}
                  cy={mouseY - boxY - 12}
                  r={3}
                  fill="#1F2937"
                />
                {/* 尾巴 / Tail */}
                <path
                  d={`M ${mouseX - boxX - mouseSize + mousePress * 80} ${mouseY - boxY} 
                      Q ${mouseX - boxX - mouseSize - 20 + mousePress * 80} ${mouseY - boxY - 30} 
                      ${mouseX - boxX - mouseSize - 15 + mousePress * 80} ${mouseY - boxY - 40}`}
                  stroke={MOUSE_COLOR}
                  strokeWidth={4}
                  fill="none"
                  strokeLinecap="round"
                />
              </g>

              {/* 标签 / Labels */}
              <text
                x={200}
                y={boxHeight + 34}
                fill={primaryColor}
                fontSize={22}
                fontWeight="bold"
                textAnchor="middle"
              >
                斯金纳箱 (Skinner Box)
              </text>

              {/* 杠杆标签 / Lever Label */}
              <text
                x={leverX - boxX + 20}
                y={leverY - boxY + 64}
                fill={ACCENT}
                fontSize={18}
                textAnchor="middle"
              >
                杠杆
              </text>

              {/* 食物槽标签 / Food Label */}
              <text
                x={foodX - boxX + 17}
                y={foodY - boxY + 45}
                fill={FOOD_COLOR}
                fontSize={18}
                textAnchor="middle"
              >
                食物
              </text>

              {/* 计数器 / Counter */}
              <text
                x={200}
                y={-10}
                fill={SUCCESS}
                fontSize={18}
                fontWeight="bold"
                textAnchor="middle"
              >
                按压次数: {pressCount}
              </text>
            </svg>

            {/* 实验说明 / Experiment Description */}
            <div
              style={{
                marginTop: -40,
                textAlign: 'center',
                fontSize: 22,
                color: '#94a3b8',
                width: boxWidth,
              }}
            >
              老鼠学习：按杠杆 → 获得食物
            </div>

            {/* 核心结论 / Key Conclusion */}
            <div
              style={{
                background: `linear-gradient(135deg, ${primaryColor}20, ${secondaryColor}20)`,
                borderRadius: 15,
                padding: 25,
                border: `2px solid ${SUCCESS}`,
                opacity: resultOpacity,
                marginTop: 25,
              }}
            >
              <div
                style={{
                  fontSize: 26,
                  fontWeight: 700,
                  color: SUCCESS,
                  marginBottom: 10,
                }}
              >
                ✨ 核心发现
              </div>
              <div
                style={{
                  fontSize: 24,
                  color: '#ffffff',
                  lineHeight: 1.6,
                }}
              >
                行为的结果影响其重复概率：积极结果（奖励）增加行为频率，消极结果（惩罚）减少行为频率。
              </div>
              <div
                style={{
                  marginTop: 12,
                  padding: '10px 18px',
                  background: 'rgba(255, 255, 255, 0.1)',
                  borderRadius: 8,
                  fontSize: 20,
                  color: ACCENT,
                  fontWeight: 600,
                  display: 'inline-block',
                }}
              >
                B.F. Skinner, 1938
              </div>
            </div>
          </div>
        </div>

        {/* 右侧：信息面板 / Right: Info Panel */}
        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: 25,
          }}
        >
          {/* 强化时间表 / Reinforcement Schedules */}
          <div
            style={{
              opacity: scheduleOpacity,
              marginTop: -20,
            }}
          >
            <div
              style={{
                fontSize: 30,
                fontWeight: 700,
                color: '#ffffff',
                marginBottom: 20,
              }}
            >
              🎯 强化时间表
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 15,
              }}
            >
              {schedules.map((schedule, index) => (
                <div
                  key={index}
                  style={{
                    background: 'rgba(255, 255, 255, 0.05)',
                    borderRadius: 12,
                    padding: '15px 20px',
                    border: `2px solid ${schedule.color}40`,
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      marginBottom: 8,
                    }}
                  >
                    <span style={{ fontSize: 30 }}>{schedule.icon}</span>
                    <span
                      style={{
                        fontSize: 28,
                        fontWeight: 700,
                        color: schedule.color,
                      }}
                    >
                      {schedule.name}
                    </span>
                  </div>
                  <div
                    style={{
                      fontSize: 26,
                      color: '#6B7280',
                      marginBottom: 10,
                    }}
                  >
                    {schedule.nameEn}
                  </div>
                  <div
                    style={{
                      fontSize: 26,
                      color: '#94a3b8',
                    }}
                  >
                    {schedule.example}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* 学习曲线 / Learning Curve */}
          <div
            style={{
              opacity: curveOpacity,
              flex: 1,
            }}
          >
            <div
              style={{
                fontSize: 30,
                fontWeight: 700,
                color: '#ffffff',
                marginBottom: 20,
                marginTop: 10,
              }}
            >
              📈 学习曲线
            </div>
            <div
              style={{
                background: 'rgba(255, 255, 255, 0.05)',
                borderRadius: 15,
                padding: 20,
                border: '2px solid rgba(255, 255, 255, 0.1)',
                height: 300,
                position: 'relative',
              }}
            >
              {/* Y轴标签 / Y-axis Label */}
              <div
                style={{
                  position: 'absolute',
                  left: -60,
                  top: '30%',
                  transform: 'rotate(-90deg) translate(-50%, -50%)',
                  fontSize: 22,
                  color: '#6B7280',
                }}
              >
                反应率 (%)
              </div>

              <svg width="100%" height="250">
                {/* 网格 / Grid */}
                {[0, 25, 50, 75, 100].map((y, i) => (
                  <g key={i}>
                    <line
                      x1="50"
                      y1={220 - y * 1.9}
                      x2="620"
                      y2={220 - y * 1.9}
                      stroke="rgba(255,255,255,0.1)"
                      strokeWidth={1}
                    />
                    <text
                      x="40"
                      y={220 - y * 1.9 + 6}
                      fill="#6B7280"
                      fontSize={18}
                      textAnchor="end"
                    >
                      {y}
                    </text>
                  </g>
                ))}

                {/* 学习曲线 / Learning Curve Path */}
                <path
                  d={`M 50,220 
                      ${learningData
                        .slice(0, Math.ceil(learningData.length * curveProgress))
                        .map((d, i) => {
                          const x = 50 + i * 70;
                          const y = 220 - d.rate * 1.9;
                          return `L ${x},${y}`;
                        })
                        .join(' ')}`}
                  stroke={SUCCESS}
                  strokeWidth={4}
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />

                {/* 数据点 / Data Points */}
                {learningData
                  .slice(0, Math.ceil(learningData.length * curveProgress))
                  .map((d, i) => {
                    const x = 50 + i * 70;
                    const y = 220 - d.rate * 1.9;
                    return (
                      <circle
                        key={i}
                        cx={x}
                        cy={y}
                        r={7}
                        fill={SUCCESS}
                      />
                    );
                  })}

                {/* X轴标签 / X-axis Label */}
                <text
                  x="340"
                  y="248"
                  fill="#6B7280"
                  fontSize={22}
                  textAnchor="middle"
                >
                  试验次数
                </text>
              </svg>
            </div>
          </div>

        </div>
      </div>
    </AbsoluteFill>
  );
};

// 默认导出 / Default Export
export default OperantConditioningAnimation;
